(function (global) {
  'use strict';

  const STORAGE_KEY = 'bodycare:wishlist:v1';
  const MAX_ITEMS = 100;
  const CHANGE_EVENT = 'bodycare:wishlist:changed';
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9-]*$/i;
  let memoryItems = [];
  let storageAvailable = true;
  let warnedAboutStorage = false;
  let lastRemoved = null;

  function normalize(items) {
    if (!Array.isArray(items)) return [];

    const newestByHandle = new Map();
    items.forEach(function (item) {
      const handle = typeof item === 'string' ? item : item && item.handle;
      if (typeof handle !== 'string') return;

      const cleanHandle = handle.trim();
      if (!HANDLE_PATTERN.test(cleanHandle)) return;
      const normalizedItem = {
        handle: cleanHandle,
        addedAt: Number(item && item.addedAt) || Date.now()
      };
      const existing = newestByHandle.get(cleanHandle);
      if (!existing || normalizedItem.addedAt > existing.addedAt) newestByHandle.set(cleanHandle, normalizedItem);
    });

    return Array.from(newestByHandle.values())
      .sort(function (a, b) { return b.addedAt - a.addedAt; })
      .slice(0, MAX_ITEMS);
  }

  function getStorage() {
    try {
      const storage = global.localStorage;
      const probe = STORAGE_KEY + ':probe';
      storage.setItem(probe, '1');
      storage.removeItem(probe);
      return storage;
    } catch (error) {
      storageAvailable = false;
      return null;
    }
  }

  function read() {
    const storage = getStorage();
    if (!storage) return memoryItems.slice();

    try {
      const raw = storage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      const clean = normalize(parsed);
      if (raw && JSON.stringify(parsed) !== JSON.stringify(clean)) {
        storage.setItem(STORAGE_KEY, JSON.stringify(clean));
      }
      memoryItems = clean;
      return clean.slice();
    } catch (error) {
      memoryItems = [];
      try { storage.setItem(STORAGE_KEY, '[]'); } catch (storageError) { storageAvailable = false; }
      return [];
    }
  }

  function write(items) {
    const clean = normalize(items);
    memoryItems = clean;
    const storage = getStorage();
    if (storage) {
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(clean));
      } catch (error) {
        storageAvailable = false;
      }
    }
    return clean.slice();
  }

  function emit(handle, action) {
    const detail = { count: api.count(), handle: handle || null, action: action || 'sync' };
    if (typeof global.CustomEvent === 'function' && typeof global.dispatchEvent === 'function') {
      global.dispatchEvent(new global.CustomEvent(CHANGE_EVENT, { detail: detail }));
    }
    updateCountBadges(detail.count);
    if (!storageAvailable && !warnedAboutStorage) {
      warnedAboutStorage = true;
      notify('Wishlist is saved for this visit only because browser storage is unavailable.');
    }
    return detail;
  }

  function has(handle) {
    return read().some(function (item) { return item.handle === handle; });
  }

  function add(handle) {
    if (typeof handle !== 'string' || !HANDLE_PATTERN.test(handle.trim())) return read();
    const cleanHandle = handle.trim();
    const existing = read().filter(function (item) { return item.handle !== cleanHandle; });
    write([{ handle: cleanHandle, addedAt: Date.now() }].concat(existing));
    lastRemoved = null;
    emit(cleanHandle, 'add');
    return read();
  }

  function remove(handle) {
    const items = read();
    const removed = items.find(function (item) { return item.handle === handle; });
    if (!removed) return items;
    lastRemoved = removed;
    write(items.filter(function (item) { return item.handle !== handle; }));
    emit(handle, 'remove');
    return read();
  }

  function toggle(handle) {
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle);
    return true;
  }

  function getAll() {
    return read();
  }

  function count() {
    return read().length;
  }

  function updateCountBadges(value) {
    if (!global.document) return;
    global.document.querySelectorAll('[data-wishlist-count]').forEach(function (badge) {
      badge.textContent = String(value);
      badge.hidden = value === 0;
      badge.setAttribute('aria-label', value + (value === 1 ? ' saved item' : ' saved items'));
    });
  }

  function ensureLiveRegion() {
    if (!global.document) return null;
    let region = global.document.getElementById('bodycare-wishlist-live-region');
    if (region) return region;
    region = global.document.createElement('div');
    region.id = 'bodycare-wishlist-live-region';
    region.className = 'visually-hidden';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    global.document.body.appendChild(region);
    return region;
  }

  function notify(message, undoHandle) {
    const region = ensureLiveRegion();
    if (region) region.textContent = message;
    if (!global.document) return;

    let toast = global.document.getElementById('bodycare-wishlist-toast');
    if (!toast) {
      toast = global.document.createElement('div');
      toast.id = 'bodycare-wishlist-toast';
      toast.className = 'bodycare-wishlist-toast';
      toast.setAttribute('role', 'status');
      global.document.body.appendChild(toast);
    }

    toast.replaceChildren(global.document.createTextNode(message));
    if (undoHandle) {
      const undo = global.document.createElement('button');
      undo.type = 'button';
      undo.className = 'bodycare-wishlist-toast__undo';
      undo.textContent = 'Undo';
      undo.addEventListener('click', function () {
        add(undoHandle);
        toast.hidden = true;
      }, { once: true });
      toast.appendChild(undo);
    }
    toast.hidden = false;
    global.clearTimeout(toast.hideTimer);
    toast.hideTimer = global.setTimeout(function () { toast.hidden = true; }, 4500);
  }

  const api = {
    key: STORAGE_KEY,
    maxItems: MAX_ITEMS,
    has: has,
    add: add,
    remove: remove,
    toggle: toggle,
    getAll: getAll,
    count: count
  };

  global.BodycareWishlist = api;

  if (global.customElements && global.HTMLElement && !global.customElements.get('wishlist-toggle')) {
    class WishlistToggle extends global.HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.button = this.querySelector('button');
        this.handle = this.dataset.productHandle;
        if (!this.button || !this.handle) return;
        this.onClick = this.onClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.button.addEventListener('click', this.onClick);
        global.addEventListener(CHANGE_EVENT, this.onChange);
        this.sync();
      }

      disconnectedCallback() {
        if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
        if (this.onChange) global.removeEventListener(CHANGE_EVENT, this.onChange);
        this.initialized = false;
      }

      onClick() {
        const saved = api.toggle(this.handle);
        this.sync();
        const productName = this.dataset.productTitle || 'Item';
        if (saved) notify(productName + ' added to your wishlist.');
        else notify(productName + ' removed from your wishlist.', this.handle);
      }

      onChange() {
        this.sync();
      }

      sync() {
        const selected = api.has(this.handle);
        this.classList.toggle('is-selected', selected);
        this.button.setAttribute('aria-pressed', String(selected));
        const label = selected ? (this.dataset.savedLabel || 'Remove from wishlist') : (this.dataset.label || 'Add to wishlist');
        this.button.setAttribute('aria-label', label);
        const text = this.querySelector('[data-wishlist-label]');
        if (text) text.textContent = selected ? (this.dataset.savedText || 'Saved') : (this.dataset.buttonText || 'Add to wishlist');
      }
    }

    global.customElements.define('wishlist-toggle', WishlistToggle);
  }

  if (typeof global.addEventListener === 'function') {
    global.addEventListener('storage', function (event) {
      if (event.key !== STORAGE_KEY) return;
      try {
        memoryItems = normalize(event.newValue ? JSON.parse(event.newValue) : []);
      } catch (error) {
        memoryItems = [];
      }
      emit(null, 'sync');
    });
    global.addEventListener('DOMContentLoaded', function () {
      updateCountBadges(api.count());
      ensureLiveRegion();
    }, { once: true });
  }
})(typeof window !== 'undefined' ? window : globalThis);
